import React, { useMemo, useRef, useState } from 'react';

import {
  Box,
  Checkbox,
  Divider,
  Heading,
  Link,
  Text,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import jwt_decode from 'jwt-decode';
import ReCAPTCHA from 'react-google-recaptcha-enterprise';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import countryList from 'react-select-country-list';

import {
  AppButton,
  FormInput,
  FormPasswordInput,
  FormPhoneInput,
  FormSelect,
} from '@/elements';
import { signupSchema } from '@/validations/signUp.schema';

import { SignupResendModal } from '@/components/Signup/resend-modal';
import { SignupSuccessModal } from '@/components/Signup/success-modal';
import { WrongCountryModal } from '@/components/Signup/wrong-country-modal';
import { FormContainer } from '@/components/form/form-container';

import { AccountTypeEnum } from '@/enum/accountType.enum';
import { AppRoutesEnum } from '@/enum/appRoutes.enum';
import { MediaQueryAmountsEnum } from '@/enum/mediaQueryAmounts.enum';

import {
  useRegisterMutation,
  useResendForConfirmEmailMutation,
} from '@/services/api.service';

import { ToastTypes, useCustomToast } from '@/hooks/useCustomToast';

import { RECAPTCHA_KEY } from '@/utils/constants';
import { getErrorMessageByCode } from '@/utils/getErrorMessage';

export interface IRegisterRequestData extends FieldValues {
  country: string | undefined;
  security: string;
}

const SignUpPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile] = useMediaQuery(MediaQueryAmountsEnum.isMobile);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isWrongCountryModalOpen, setIsWrongCountryModalOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const customToast = useCustomToast();

  const countriesList = useMemo(() => countryList().getData(), []);

  const [signUp, { isLoading }] = useRegisterMutation();
  const [resendLink, { isLoading: isResendLinkLoading }] =
    useResendForConfirmEmailMutation();

  const captchaElement = useRef<ReCAPTCHA | null>();

  const [searchParams] = useSearchParams();

  const jwt = searchParams.get('hash');

  const decodedJwt: any = jwt ? jwt_decode(jwt) : null;

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(signupSchema(t)),
  });

  const {
    handleSubmit,
    register,
    watch,
    formState: { isValid, errors },
  } = methods;

  const countryValue = String(watch('country', 'us'));

  const onCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate(AppRoutesEnum.main);
  };

  const onCloseResendModal = () => {
    setIsResendModalOpen(false);
  };

  const onCloseWrongCountryModal = () => {
    setIsWrongCountryModalOpen(false);
    navigate(AppRoutesEnum.main);
  };

  const onSubmit: SubmitHandler<FieldValues> = async (userData) => {
    if (!captchaElement.current) {
      throw new Error('Invalid capture');
    }
    const captureValue = await captchaElement.current?.executeAsync();

    setUserEmail(userData.email);

    userData.accountType = AccountTypeEnum.business;
    userData.security = captureValue || '';

    if (jwt) {
      if (decodedJwt.mId) {
        userData.merchantId = decodedJwt.mId;
      }
      if (decodedJwt.mAdmin) {
        userData.isMerchantAdmin = decodedJwt.mAdmin;
      }
      if (decodedJwt.admin) {
        userData.isUserAdmin = decodedJwt.admin;
      }
    }

    await signUp(userData as IRegisterRequestData)
      .unwrap()
      .then(() => {
        setIsSuccessModalOpen(true);
      })
      .catch((e) => {
        if (e.data.code === 1105) {
          setIsResendModalOpen(true);
        }
        if (e.data.code === 1106) {
          setIsWrongCountryModalOpen(true);
        }

        customToast(t(getErrorMessageByCode(e.data.code)), ToastTypes.error);
      });
  };

  const onResendLink = async () => {
    if (!captchaElement.current) {
      throw new Error('Invalid capture');
    }
    const captureValue = await captchaElement.current?.executeAsync();

    await resendLink({
      security: captureValue || '',
      email: userEmail,
    })
      .unwrap()
      .then(() => {
        customToast(t('signup.resendModal.successMessage'), ToastTypes.success);
      })
      .catch((e) => {
        customToast(t(getErrorMessageByCode(e.data.code)), ToastTypes.error);
      });
  };

  return (
    <>
      <FormContainer disableShadow pb='0px'>
        <Heading as='h3' color='app.pink' pb='md' size={isMobile ? 'md' : 'md'}>
          {t('signup.description.title')}
        </Heading>
        <Text mb='md'>{t('signup.description.line1')}</Text>
        <Text mb='md'>{t('signup.description.line2')}</Text>
        <Text>
          {t('signup.description.line3')}{' '}
          <AppButton
            as={Link}
            href='mailto:support@remuno.com?subject=Support Request'
            isExternal
            variant='link'
          >
            support@remuno.com
          </AppButton>
        </Text>
      </FormContainer>

      <FormContainer mt='lg' pt='0px'>
        <Heading as='h1' color='app.pink' pb='lg' size={isMobile ? 'md' : 'lg'}>
          {t('signup.title')}
        </Heading>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormInput
              autoComplete='name'
              isDisabled={isLoading}
              label={t('signup.name.label')}
              name='name'
            />

            {jwt && decodedJwt.mName ? (
              <FormInput
                label={t('signup.merchantName.label')}
                name='merchantName'
                value={decodedJwt.mName}
                variant='readonly'
              />
            ) : (
              <FormInput
                autoComplete='organization'
                isDisabled={isLoading}
                label={t('signup.merchantName.label')}
                name='merchantName'
              />
            )}

            {jwt && decodedJwt.email ? (
              <FormInput
                label={t('signup.email.label')}
                name='email'
                value={decodedJwt.email}
                variant='readonly'
              />
            ) : (
              <FormInput
                autoComplete='email'
                isDisabled={isLoading}
                label={t('signup.email.label')}
                name='email'
              />
            )}

            <FormSelect
              isDisabled={isLoading}
              isSearchable
              label={t('signup.country.label')}
              name='country'
              options={countriesList}
            />

            <FormPhoneInput
              country={countryValue.toLocaleLowerCase()}
              isDisabled={isLoading}
              label={t('signup.mobileNumber.label')}
              name='mobileNumber'
            />

            <FormPasswordInput
              autoComplete='new-password'
              isDisabled={isLoading}
              isStrengthBarNeeded
              label={t('signup.password.label')}
              name='password'
            />

            <FormPasswordInput
              isDisabled={isLoading}
              label={t('signup.confirmPassword.label')}
              name='confirmPassword'
            />

            <VStack alignItems='stretch' spacing='lg'>
              <Checkbox
                isInvalid={Boolean(errors['termsAgree'])}
                {...register('termsAgree')}
              >
                <Box fontSize='sm' ml='sm'>
                  <Trans
                    components={{
                      UserAgreementLink: (
                        <Link
                          as={RouterLink}
                          color='app.linkBlue'
                          isExternal
                          textDecoration='underline'
                          to='/documents/termsOfUse.pdf'
                        />
                      ),
                      MerchantAgreementLink: (
                        <Link
                          color='app.linkBlue'
                          href='https://alt5sigma.com/termsofuse/'
                          isExternal
                          textDecoration='underline'
                        />
                      ),
                      PrivacyPolicyLink: (
                        <Link
                          as={RouterLink}
                          color='app.linkBlue'
                          isExternal
                          textDecoration='underline'
                          to='/documents/privacyPolicy.pdf'
                        />
                      ),
                    }}
                    i18nKey='signup.checkbox.termsAndConditions'
                  />
                </Box>
              </Checkbox>

              <Checkbox
                isInvalid={Boolean(errors['acceptMarketing'])}
                {...register('acceptMarketing')}
              >
                <Box fontSize='sm' ml='sm'>
                  {t('signup.checkbox.acceptMarketing')}
                </Box>
              </Checkbox>

              <Box textAlign='center'>
                {RECAPTCHA_KEY && (
                  <ReCAPTCHA
                    ref={(el: any) => (captchaElement.current = el)}
                    sitekey={RECAPTCHA_KEY}
                    size='invisible'
                  />
                )}

                <AppButton
                  isDisabled={!isValid || isLoading}
                  isLoading={isLoading}
                  type='submit'
                  variant='primary'
                >
                  {t('signup.button')}
                </AppButton>
              </Box>

              <Divider />

              <Box fontSize='sm'>
                {t('signup.alreadyHaveAccount')}&nbsp;
                <Link
                  as={RouterLink}
                  color='app.linkBlue'
                  textDecoration='underline'
                  to={AppRoutesEnum.login}
                >
                  {t('common.signIn')}
                </Link>
              </Box>
            </VStack>
          </form>
        </FormProvider>
      </FormContainer>

      <SignupSuccessModal
        email={userEmail}
        isOpen={isSuccessModalOpen}
        onClose={onCloseSuccessModal}
      />

      <SignupResendModal
        email={userEmail}
        isLoading={isResendLinkLoading}
        isOpen={isResendModalOpen}
        onClose={onCloseResendModal}
        onResend={onResendLink}
      />

      <WrongCountryModal
        isOpen={isWrongCountryModalOpen}
        onClose={onCloseWrongCountryModal}
      />
    </>
  );
};

export default SignUpPage;
